import { forwardRef, useEffect, useImperativeHandle, useRef, type ReactElement } from 'react';
import type { FlatList as RNFlatList, NativeScrollEvent, NativeSyntheticEvent } from 'react-native';

import { SupportMessageBubble } from './SupportMessageBubble';
import {
  failureReasonFor,
  isRetryableFailure,
  type ThreadItem,
  type ThreadMessageItem,
} from './threadItems';

import { FlatList } from '@/components/ui/flat-list';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import type { InquiryForwardTarget } from '@/services/conversations/inquiryForward';
import type { MessageLinkKind } from '@/util/messageLinks';

const LIST_EDGE_PADDING = 12;

/**
 * A support thread arrives whole, so every message is drawn up front. Drawn in
 * batches, the first scroll to the end landed at the end of the first batch
 * rather than at the newest message.
 */
const MIN_INITIAL_RENDER = 10;

/** Within this distance of the end, the reader counts as reading the latest messages. */
const STICK_TO_END_DISTANCE = 80;

export type SupportMessageListHandle = {
  /** Brings the newest message into view and keeps following new ones. */
  scrollToLatest: () => void;
};

type SupportMessageListProps = {
  items: ThreadItem[];
  counterpartName: string;
  counterpartAvatarUrl?: string;
  /** A transparent header's height over the top of the list. */
  topInset?: number;
  /** A floating composer's height over the bottom of the list. */
  bottomInset?: number;
  onRetry: (message: ThreadMessageItem) => void;
  onDiscard: (message: ThreadMessageItem) => void;
  onOpenReference: (conversationId: string) => void;
  onOpenForwardInquiry: (message: ThreadMessageItem, target: InquiryForwardTarget) => void;
  onOpenSurvey: (message: ThreadMessageItem) => void;
  onOpenLink: (href: string, kind: MessageLinkKind) => void;
  /** Shown in place of the messages while the thread has none. */
  emptyState?: ReactElement;
};

/**
 * The thread, oldest first, opening on the newest message.
 *
 * Deliberately not an inverted list. Inverting flips the scroll view with a
 * transform, and under a transparent iOS 26 header the system's scroll edge
 * effect is applied to the flipped view and blurs the whole thread. A plain
 * list that follows its end, as Research's thread does, keeps the effect at
 * the top edge where it belongs.
 *
 * It follows the end only while the reader is there: new messages, a growing
 * composer and an opening keyboard keep the latest message in view, but a
 * reader scrolled back through history stays where they are.
 *
 * The space below the last message is a footer, not bottom padding: the
 * list's scroll to the end counts its rows and footer and ignores the content
 * container's padding, so padding left the newest message under the
 * composer.
 */
export const SupportMessageList = forwardRef<SupportMessageListHandle, SupportMessageListProps>(
  function SupportMessageList(
    {
      items,
      counterpartName,
      counterpartAvatarUrl,
      topInset = 0,
      bottomInset = 0,
      onRetry,
      onDiscard,
      onOpenReference,
      onOpenForwardInquiry,
      onOpenSurvey,
      onOpenLink,
      emptyState,
    },
    ref
  ) {
    const listRef = useRef<RNFlatList<ThreadItem>>(null);

    const isFollowingEnd = useRef(true);

    const hasShownContent = useRef(false);

    const isDragging = useRef(false);

    const followEnd = (animated: boolean) => {
      if (isFollowingEnd.current) {
        listRef.current?.scrollToEnd({ animated });
      }
    };

    useImperativeHandle(ref, () => ({
      scrollToLatest: () => {
        isFollowingEnd.current = true;

        listRef.current?.scrollToEnd({ animated: true });
      },
    }));

    /*
     * The composer's height is measured after the first scroll to the end, so
     * re-pin once it is known.
     */
    useEffect(() => {
      followEnd(false);
    }, [bottomInset]);

    /*
     * Only the reader's own scrolling decides whether to follow the end. iOS
     * also reports the list's own scrolls to the end as a momentum end, at a
     * position the next row or the composer's padding has already moved past,
     * so a momentum end counts only when a drag started it.
     */
    const settleAfterDrag = ({ nativeEvent }: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentOffset, contentSize, layoutMeasurement } = nativeEvent;

      const distanceFromEnd = contentSize.height - contentOffset.y - layoutMeasurement.height;

      isFollowingEnd.current = distanceFromEnd < STICK_TO_END_DISTANCE;
    };

    const itemCount = useRef(items.length);

    return (
      <FlatList
        ref={listRef}
        className="flex-1"
        data={items}
        keyExtractor={(item: ThreadItem) => item.key}
        initialNumToRender={Math.max(items.length, MIN_INITIAL_RENDER)}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{
          paddingTop: topInset + LIST_EDGE_PADDING,
          flexGrow: items.length === 0 ? 1 : undefined,
        }}
        ListEmptyComponent={emptyState}
        ListFooterComponent={<View style={{ height: bottomInset + LIST_EDGE_PADDING }} />}
        scrollIndicatorInsets={{ top: topInset, bottom: bottomInset }}
        contentInsetAdjustmentBehavior="never"
        onScrollBeginDrag={() => {
          isDragging.current = true;

          isFollowingEnd.current = false;
        }}
        onScrollEndDrag={settleAfterDrag}
        onMomentumScrollEnd={(event) => {
          if (isDragging.current) {
            isDragging.current = false;

            settleAfterDrag(event);

            return;
          }

          // The list's own animated scroll aimed at an end estimated before the
          // newest row was measured; finish the last few points without motion.
          followEnd(false);
        }}
        onContentSizeChange={() => {
          const hasNewItems = items.length !== itemCount.current;

          itemCount.current = items.length;

          followEnd(hasShownContent.current && hasNewItems);

          hasShownContent.current = true;
        }}
        onLayout={() => followEnd(false)}
        renderItem={({ item }: { item: ThreadItem }) => {
          if (item.kind === 'day') {
            return (
              <View className="items-center py-2">
                <Text className="font-medium text-xs text-typography-700">{item.label}</Text>
              </View>
            );
          }

          const reference = item.referenceConversationId;

          const failureReason = failureReasonFor(item.failureCode);

          return (
            <SupportMessageBubble
              body={item.body}
              createdAt={item.createdAt}
              isOwn={item.isOwn}
              authorLabel={item.isOwn ? undefined : counterpartName}
              avatarUrl={item.isOwn ? undefined : counterpartAvatarUrl}
              status={item.status}
              hasReference={Boolean(reference)}
              onOpenReference={reference ? () => onOpenReference(reference) : undefined}
              referencedInquiry={item.referencedInquiry}
              sharedSurvey={item.sharedSurvey}
              onOpenSurvey={item.sharedSurvey ? () => onOpenSurvey(item) : undefined}
              onOpenLink={onOpenLink}
              inquiryForward={item.inquiryForward}
              onOpenForwardInquiry={
                item.inquiryForward ? (target) => onOpenForwardInquiry(item, target) : undefined
              }
              onRetry={isRetryableFailure(item.failureCode) ? () => onRetry(item) : undefined}
              onDiscard={() => onDiscard(item)}
              failureReason={failureReason}
              files={item.files}
            />
          );
        }}
      />
    );
  }
);
